import React from 'react'
import { CircularProgress } from '@material-ui/core'
import { SubNavButton } from './navigation'
import SendToManager from './EnabledCommunication/SendToManager'
import queryString from 'query-string'
import { DocumentStatus } from '../utils/documentHelper'
import reviewTypeConstants from '../constants/reviewType.constants'
import { getLocalisedDateTimeStr } from '../helpers/dateTime'
import { dateTimeFormatOptionsConstants } from '../constants/datetime.constants'

type WizardNavButtonsProps = {
    // Location/Query
    location: {
        search: string
    }

    // State
    documentStatus: number | null
    documentData: {
        documentGuid: string
        documentName?: string
        [key: string]: any
    }
    progress: number
    lastAutoSavedDate: string | null
    isSaving: boolean
    isCompleting: boolean
    isDownloading: boolean
    isSendToManagerOpen: boolean

    // Display flags
    showSendToManagerButton: boolean

    // Computed document states
    documentCompleted: boolean
    documentInReview: boolean
    documentReviewEnded: boolean
    documentInProgress: boolean

    // User/Role states
    isAdviser: boolean
    isHrUser: boolean
    hasHrUserRole: boolean

    // Document config
    reviewType: number

    // Data
    caseParticipants: {
        activeCaseParticipants?: any[]
    }
    caseId?: string

    // values
    userCanEditFullDocument: boolean
    canComplete: boolean
    docIsAdviserDependant: boolean

    // Handlers
    handleEditClick: () => void
    saveDocument: (options: {
        returnAfterSave: boolean
        autoSaveDocument?: boolean
    }) => void
    setInReview: () => void
    endReview: () => void
    previewDocument: () => void
    downloadDocument: () => void
    onSendToManagerClick: () => void
    onCloseSendToManager: () => void
    onCompleteClick: () => void
    onSendForReviewClick: () => void

    // Styles
    classes: {
        modalSaveButtonProgress: string
    }
}

const LastSavedDisplay = ({
    lastAutoSavedDate,
}: {
    lastAutoSavedDate: string | null
}) => {
    if (!lastAutoSavedDate) {
        return null
    }

    return (
        <>
            Last saved{' '}
            {getLocalisedDateTimeStr(
                lastAutoSavedDate,
                dateTimeFormatOptionsConstants.DEFAULT_DATE_TIME
            )}
        </>
    )
}

const WizardNavButtons = ({
    location,
    documentStatus,
    documentData,
    progress,
    lastAutoSavedDate,
    isSaving,
    isCompleting,
    isDownloading,
    isSendToManagerOpen,
    showSendToManagerButton,
    documentCompleted,
    documentInReview,
    documentReviewEnded,
    documentInProgress,
    isAdviser,
    isHrUser,
    hasHrUserRole,
    reviewType,
    caseParticipants,
    caseId,
    userCanEditFullDocument,
    canComplete,
    docIsAdviserDependant,
    handleEditClick,
    saveDocument,
    setInReview,
    endReview,
    previewDocument,
    downloadDocument,
    onSendToManagerClick,
    onCloseSendToManager,
    onCompleteClick,
    onSendForReviewClick,
    classes,
}: WizardNavButtonsProps) => {
    const parsedQuery = queryString.parse(location.search)
    const { isUserCaseParticipant } = parsedQuery

    // TODO
    // Toggle this to "true" when client applications pass the value on the QueryString
    // i.e. in the final version, if not supplied, assume they are a Case Participant
    // In initial version, assume the end user is NOT a Case Participant if the value is not on the QueryString
    // (and that they won't End Review on a Case if they are)
    const isUserCaseParticipantDefaultValue = false

    const isUserCaseParticipantValue =
        isUserCaseParticipant === undefined || isUserCaseParticipant === null
            ? isUserCaseParticipantDefaultValue
            : isUserCaseParticipant === 'true'

    const userCanEndReview = hasHrUserRole && !isUserCaseParticipantValue
    const documentCanEndReview = !documentReviewEnded && !documentCompleted
    const canEndReview = userCanEndReview && documentCanEndReview

    const hideSaveAsDraftButton =
        documentCompleted || isCompleting || isSaving || isDownloading

    const hideSendForReviewButton =
        isSaving || documentInReview || isCompleting || documentCompleted

    const handleSaveDraft = () => {
        saveDocument({ returnAfterSave: false })
    }

    const renderCompleteOption = () => {
        if (
            !isHrUser &&
            !documentCompleted &&
            !hideSendForReviewButton &&
            (reviewType === reviewTypeConstants.AdviserCompletion ||
                (reviewType === reviewTypeConstants.AdviserReview &&
                    documentStatus !== DocumentStatus.ReviewEnded))
        ) {
            return (
                <SubNavButton
                    handleClick={onSendForReviewClick}
                    label='Send For Review'
                    disabled={hideSendForReviewButton}
                />
            )
        }

        if (canComplete) {
            const shouldDisable =
                documentCompleted ||
                progress < 100 ||
                documentInReview ||
                isCompleting ||
                isSaving ||
                isDownloading

            if (shouldDisable) {
                return null // Hide the button instead of showing it disabled
            }

            return (
                <SubNavButton
                    handleClick={onCompleteClick}
                    label='Complete'
                    disabled={false}
                />
            )
        }

        return null
    }

    return (
        <>
            {showSendToManagerButton && !documentInReview && (
                <>
                    <SendToManager
                        open={isSendToManagerOpen}
                        onClose={onCloseSendToManager}
                        caseParticipants={caseParticipants}
                        caseId={caseId}
                        documentData={documentData}
                        saveDraft={handleSaveDraft}
                    />
                    <SubNavButton
                        handleClick={onSendToManagerClick}
                        label={'Send to Manager'}
                        disabled={documentInReview}
                    />
                </>
            )}
            {userCanEditFullDocument && (
                <SubNavButton
                    handleClick={handleEditClick}
                    label={'Edit'}
                    disabled={false}
                />
            )}
            {!hideSaveAsDraftButton && (
                <SubNavButton
                    handleClick={saveDocument}
                    label={'Save as draft'}
                    disabled={hideSaveAsDraftButton}
                />
            )}
            {isAdviser &&
                docIsAdviserDependant &&
                (documentInProgress ? (
                    <SubNavButton
                        handleClick={setInReview}
                        label={'Set To In Review'}
                    />
                ) : (
                    <>
                        {canEndReview && (
                            <SubNavButton
                                handleClick={endReview}
                                label='End Review'
                            />
                        )}
                    </>
                ))}
            {renderCompleteOption()} &nbsp;
            {(isCompleting || isSaving || isDownloading) && (
                <CircularProgress
                    size={24}
                    className={classes.modalSaveButtonProgress}
                />
            )}
            {documentStatus !== DocumentStatus.Completed && (
                <SubNavButton
                    handleClick={previewDocument}
                    label={'Preview'}
                    disabled={isCompleting || isSaving || isDownloading}
                />
            )}
            {documentStatus === DocumentStatus.Completed && (
                <SubNavButton
                    handleClick={downloadDocument}
                    label={'Download'}
                    disabled={isCompleting || isSaving || isDownloading}
                />
            )}
            <span style={{ marginLeft: '15px' }}>
                <LastSavedDisplay lastAutoSavedDate={lastAutoSavedDate} />
            </span>
        </>
    )
}

export default WizardNavButtons
